/** biome-ignore-all lint/a11y/noStaticElementInteractions: <explanation> */
import React from 'react';
import { observable, Observe } from 'rewire-core';
import { WithStyle, IActionMenuItem, editor } from 'rewire-ui';
import classNames from 'classnames';
import { Theme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import MenuButton from '../ui/MenuButton';
import { capitalize } from '../utility/StringUtils';
import BatchStatusLabel from './BatchStatusLabel';
import { makeFriendlyDate, localTimeZoneUTCNow } from '../utility/DateUtils';
import dailyTimecardModel, { DailyTimecardModelBase } from './DailyTimecardModel';
import EditTimecardView from './edit/EditTimecardView';
import AddUdfIcon from '@material-ui/icons/PlaylistAdd';
import DeleteIcon from '@material-ui/icons/DeleteOutlined';
import AddIcon from '@material-ui/icons/AddCircleOutline';
import LogIcon from '@material-ui/icons/AssignmentOutlined';
import AddCommentDialog from '../schedule/AddCommentDialog';
import commentDialogModel from '../schedule/CommentDialogModel';
import { withStyles } from '../utility/styles';
import AccordionView from './AccordionView';
import authService from '../services/AuthService';

const CheckBox = editor('checked');

const styles = (theme: Theme) => ({
  root: {
    flexShrink: '0',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    marginBottom: '0px',
  },
  header: {
    display: 'flex',
    flex: '1 0 auto',
    alignItems: 'center',
    paddingBottom: '0px',
    marginBottom: '0px',
  },
  date: {
    color: '#4f778a',
    fontWeight: '300',
    fontSize: '15px',
    marginLeft: '12px',
  },
  futureDate: {
    fontStyle: 'italic',
    color: '#bfbfbf',
  },
  batchLabel: {
    alignItems: 'baseline',
    marginLeft: '12px',
  },
  buttonWrapper: {
    display: 'flex',
    flex: '1 0 auto',
    justifyContent: 'flex-end',
  },
  noResults: {
    fontStyle: 'italic',
    margin: '10px',
  },
  lastEdited: {
    color: '#4f778a',
    fontSize: 'smaller',
  },
  headingLeft: {
    display: 'flex',
    flexDirection: 'column',
  },
  headingLine: {
    alignItems: 'baseline',
  },
  subHeadingText: {},
  subHeadingTextReversal: {
    color: theme.palette.error.main,
  },
});

interface IDailyTimecardProps {
  model: DailyTimecardModelBase;
  hideBatchStatus?: boolean;
  hideTimecardStatus?: boolean;
  disableComments?: boolean;
}

type DailyTimecardProps = WithStyle<ReturnType<typeof styles>, IDailyTimecardProps>;

function timecardTransitionItems(model: any): IActionMenuItem[] {
  const allowableTransitions = model.selectedRolledUpTimecard?.allowableTransitions || [];
  if (!allowableTransitions.length) return [];

  const items: IActionMenuItem[] = [];
  for (const transition of allowableTransitions) {
    items.push({
      name: transition.event,
      title: capitalize(transition.event),
      closeOnClick: true,
      disabled: () => !transition.enabled,
      onClick: () => model.executeTransition(transition.event),
    });
  }
  return items;
}

const dailyAccordionModel = observable({ expanded: true });

const DailyTimecardHeaderView = (props: DailyTimecardProps) => {
  const { classes } = props;

  return (
    <span className={classNames(classes.header)}>
      <div className={classes.headingLeft}>
        <div className={classes.headingLine}>
          <Observe
            render={() => {
              const selectedRolledUpTimecard = props.model.selectedRolledUpTimecard;
              const subHeading = selectedRolledUpTimecard?.isCorrection ? (
                <span className={classes.subHeadingText}>(Correction)</span>
              ) : selectedRolledUpTimecard?.isReversal ? (
                <span className={classNames(classes.subHeadingText, classes.subHeadingTextReversal)}>(Reversal)</span>
              ) : (
                ''
              );

              return (
                <Typography display="inline" variant="h5">
                  Daily Timecard {subHeading}
                </Typography>
              );
            }}
          />
          &nbsp;
          <Observe
            render={() => {
              const selectedRolledUpTimecard = props.model.selectedRolledUpTimecard;
              const earnedDate = makeFriendlyDate(selectedRolledUpTimecard?.earnedDate);
              const futureDate = selectedRolledUpTimecard
                ? selectedRolledUpTimecard.earnedDate > localTimeZoneUTCNow().endOfDay()
                  ? classes.futureDate
                  : ''
                : '';
              return (
                <Typography display="inline" variant="h6" className={classNames(futureDate, classes.date)}>
                  {earnedDate}
                </Typography>
              );
            }}
          />
        </div>
        <div className={classes.headingLine}>
          <Observe
            render={() => {
              const selectedRolledUpTimecard = props.model.selectedRolledUpTimecard;
              const lastEdited = selectedRolledUpTimecard?.lastEdited;

              return (
                <Typography className={classes.lastEdited} display="inline">
                  {lastEdited}
                </Typography>
              );
            }}
          />
        </div>
      </div>

      {!props.hideBatchStatus && (
        <Observe
          render={() => {
            const selectedRolledUpTimecard = props.model.selectedRolledUpTimecard;
            const batchStatus = selectedRolledUpTimecard?.batchStatus;
            const batchName = selectedRolledUpTimecard?.batchName;
            return batchStatus ? (
              <BatchStatusLabel className={classes.batchLabel} batchStatus={batchStatus} batchName={batchName} showLabel={true} />
            ) : null;
          }}
        />
      )}

      {!props.hideTimecardStatus && dailyAccordionModel.expanded && (
        <Observe
          render={() => (
            <div className={classes.buttonWrapper} onClick={(evt) => evt.stopPropagation()}>
              <CheckBox
                className={props.model.canSetManualTimecard() ? '' : 'disabled '}
                field={{
                  name: 'manualTimecard',
                  label: 'Override Rules',
                  value: props.model.manualTimecard,
                  disabled: () => !props.model.canSetManualTimecard(),
                }}
                visible={authService.evalSecurityToken('manual_timecards')}
                onValueChange={(value: boolean) => {
                  props.model.manualTimecard = value;
                }}
              />

              <Observe
                render={() => {
                  const canTransitionTimecard = props.model.canTransitionTimecard;
                  const timecardStatus = props.model.selectedRolledUpTimecard?.status;
                  const isPayable = props.model.timecardModel.isPayable;

                  return timecardStatus ? (
                    <MenuButton
                      text={timecardStatus}
                      items={timecardTransitionItems(props.model)}
                      disabled={!canTransitionTimecard || !isPayable}
                    />
                  ) : null;
                }}
              />
            </div>
          )}
        />
      )}
    </span>
  );
};

class DailyTimecardView extends React.PureComponent<DailyTimecardProps> {
  constructor(props: DailyTimecardProps) {
    super(props);
  }

  componentWillUnmount() {
    this.props.model.reset();
  }

  get timecardTransitionItems(): IActionMenuItem[] {
    const allowableTransitions = this.props.model.selectedRolledUpTimecard?.allowableTransitions || [];
    if (!allowableTransitions.length) return [];

    const items: IActionMenuItem[] = [];
    for (const transition of allowableTransitions) {
      items.push({
        name: transition.event,
        title: capitalize(transition.event),
        closeOnClick: true,
        disabled: () => !transition.enabled,
        onClick: () => this.props.model.executeTransition(transition.event),
      });
    }
    return items;
  }

  addComment = () => {
    if (this.props.disableComments || !this.props.model.selectedRolledUpTimecard) return;
    const timecard = this.props.model.timecardDefault;
    if (!timecard) return;
    commentDialogModel.load([
      {
        level: 'info',
        jobId: timecard.job?.id,
        groupId: timecard.group?.id,
        employeeId: timecard.employee?.id ?? timecard.job?.employee?.id,
        effective: this.props.model.editTimecard.earnedDate,
        type: 'user',
      },
    ]);
  };

  viewTimecardAuditLog = () => {
    dailyTimecardModel.loadAuditLogDialog('timecard');
  };

  viewEmployeeTimecardAuditLog = () => {
    dailyTimecardModel.loadAuditLogDialog('employee_timecard');
  };

  renderTimecardEditor = React.memo((): JSX.Element | null => {
    const model = this.props.model.editTimecard;

    const undoReversalItem = {
      name: 'undoReversal',
      visible: () => this.props.model.canUndoReversal,
      title: 'Undo Reversal',
      icon: dailyTimecardModel.canUndoReversal ? AddIcon : DeleteIcon,
      onClick: dailyTimecardModel.undoReversal,
      closeOnClick: true,
    };

    const moreItems = [
      {
        name: 'addComment',
        disabled: () => !this.props.model.canAddComment,
        visible: !this.props.disableComments,
        title: 'Add Comment',
        icon: AddIcon,
        onClick: this.addComment,
        closeOnClick: true,
      },
      { name: 'udfs', disabled: false, title: 'UDFs', icon: AddUdfIcon, onClick: () => model.udfEditor.open(), closeOnClick: true },
      {
        name: 'deleteTimecard',
        disabled: () => !this.props.model.earnedDate || !this.props.model.canDeleteTimecard,
        title: 'Delete',
        icon: DeleteIcon,
        onClick: model.deleteTimecard,
        closeOnClick: true,
      },
      {
        ...undoReversalItem,
      },
      {
        name: 'auditLogTimecard',
        disabled: () => !this.props.model.earnedDate,
        title: 'View Timecard Audit Log',
        icon: LogIcon,
        onClick: this.viewTimecardAuditLog,
        closeOnClick: true,
      },
      {
        name: 'auditLogEmployeeTimecard',
        disabled: () => !this.props.model.earnedDate,
        title: 'View Timecard Status Audit Log',
        icon: LogIcon,
        onClick: this.viewEmployeeTimecardAuditLog,
        closeOnClick: true,
      },
    ];

    return (
      <Observe
        render={() =>
          this.props.model.selectedRolledUpTimecard ? (
            <EditTimecardView
              model={model}
              condensedTimecard
              showPremiums
              breakdownMode
              showBreaks
              showUdfs
              moreItems={moreItems}
            />
          ) : null
        }
      />
    );
  });

  render() {
    const { classes } = this.props;

    return (
      <div className={classNames(classes.root)}>
        <div>
          <AccordionView model={dailyAccordionModel} title={(_props: any) => <DailyTimecardHeaderView {...this.props} />}>
            <this.renderTimecardEditor />

            <Observe
              render={() => {
                const selectedRolledUpTimecard = this.props.model.selectedRolledUpTimecard;
                if (!selectedRolledUpTimecard) {
                  return (
                    <Typography className={classes.noResults} variant="subtitle1">
                      No timecard selected
                    </Typography>
                  );
                }
                return null;
              }}
            />
          </AccordionView>
        </div>
        {!this.props.disableComments && <AddCommentDialog model={commentDialogModel} />}
      </div>
    );
  }
}

export default withStyles<IDailyTimecardProps>(styles, DailyTimecardView);
